import React, { useState, useRef, useEffect } from "react";
import { useLocation } from "../context/LocationContext";

// The location dropdown panel: a search box, an optional "Bridge" (all-locations)
// entry, and the filtered list of locations. Shared by the sidebar switcher and
// the page-title switchers (Appointments / Order Workflow), so the search behaves
// the same everywhere. Picking a DIFFERENT location hard-reloads the page so every
// view re-fetches data scoped to that location (no stale cross-location data).
//
// Reuses the existing .location-dropdown / .location-dropdown-item styles; only
// the search box + empty state are new (LocationSwitcher.css).
function LocationDropdownPanel({ onClose }) {
  const {
    locations,
    selectedLocationId,
    isBridgeMode,
    hasBridgeAccess,
    selectLocation,
  } = useLocation();
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, []);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? locations.filter(
        (l) =>
          (l.name || "").toLowerCase().includes(q) ||
          (l.address || "").toLowerCase().includes(q),
      )
    : locations;

  const choose = (id) => {
    onClose?.();
    // No change → don't reload.
    const current = isBridgeMode ? "bridge" : selectedLocationId;
    const target = id === null ? "bridge" : id;
    if (target === current) return;
    selectLocation(id); // validates access + persists to localStorage (sync)
    window.location.reload(); // full reload re-fetches everything for the new location
  };

  return (
    <div className="location-dropdown">
      <div className="location-dropdown-search-wrap">
        <input
          ref={searchRef}
          type="text"
          className="location-dropdown-search"
          placeholder="Search locations…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {hasBridgeAccess && !q && (
        <>
          <button
            className={`location-dropdown-item bridge-item${isBridgeMode ? " active" : ""}`}
            onClick={() => choose(null)}
          >
            <span className="location-dropdown-item-icon bridge-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>
            </span>
            <div className="location-dropdown-item-details">
              <span className="location-dropdown-item-name">The Bridge</span>
              <span className="location-dropdown-item-address">All locations</span>
            </div>
            {isBridgeMode && <span className="location-dropdown-check">✓</span>}
          </button>
          <div className="location-dropdown-separator" />
        </>
      )}

      <div className="location-dropdown-header">Locations</div>
      {filtered.map((loc) => (
        <button
          key={loc.id}
          className={`location-dropdown-item ${!isBridgeMode && loc.id === selectedLocationId ? "active" : ""}`}
          onClick={() => choose(loc.id)}
        >
          <span
            className="location-dropdown-item-icon"
            style={{ "--icon-url": "url(/images/Icons/SVG/Location_Icon.svg)" }}
          />
          <div className="location-dropdown-item-details">
            <span className="location-dropdown-item-name">{loc.name}</span>
            {loc.address && (
              <span className="location-dropdown-item-address">{loc.address}</span>
            )}
          </div>
          {!isBridgeMode && loc.id === selectedLocationId && (
            <span className="location-dropdown-check">✓</span>
          )}
        </button>
      ))}
      {filtered.length === 0 && (
        <div className="location-dropdown-empty">No locations found</div>
      )}
    </div>
  );
}

export default LocationDropdownPanel;
